import React from "react";
import { useLocation, useSearchParams } from "react-router";

interface UserBasedRouteOptions {
  internal: React.ComponentType;
  external: React.ComponentType;
  adminOne?: React.ComponentType;
}

export const userTypeRouting = (opts: UserBasedRouteOptions) => {
  if (opts.adminOne && site.permissions.canAccessAdminOne) {
    return opts.adminOne;
  }

  if (!site.currentEnvironment) {
    return opts.external;
  }

  if (site.currentEnvironment.type === "internal") {
    return opts.internal;
  }

  return opts.external;
};

interface Tab<TKey extends string> {
  key: TKey;
  label: string;
  Component: React.ComponentType;
}

// The first tab is the default and has no "tab" search param
export const useTabRoute = <TKey extends string>(tabs: readonly Tab<TKey>[]) => {
  const [defaultTab] = tabs;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const activeTab = tabs.find(tab => tab.key === searchParams.get("tab")) ?? defaultTab;

  const getTabSearchParams = (key: TKey, current: URLSearchParams) => {
    const next = new URLSearchParams(current);
    if (key === defaultTab.key) {
      next.delete("tab");
    } else {
      next.set("tab", key);
    }
    return next;
  };

  const onTabChange = (key: TKey) => {
    setSearchParams(prev => getTabSearchParams(key, prev));
  };

  const getTabHref = (key: TKey) => {
    const search = getTabSearchParams(key, searchParams).toString();
    return search ? `${location.pathname}?${search}` : location.pathname;
  };

  return { activeTab, onTabChange, getTabHref };
};
